import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { MagnifyingGlassIcon, FunnelIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

interface Equipment {
  _id: string;
  name: {
    en: string;
    ar?: string;
  };
  category?: 'utensil' | 'cookware' | 'appliance' | 'tool' | 'other';
  iconUrl?: string;
  active: boolean;
  order: number;
}

export default function EquipmentTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    loadEquipment();
  }, [activeSearch, categoryFilter, activeFilter]);

  const loadEquipment = async () => {
    try {
      setLoading(true);
      const params: any = {};
      
      if (activeSearch) params.search = activeSearch;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (activeFilter === 'active') params.activeOnly = 'true';
      if (activeFilter === 'inactive') params.activeOnly = 'false';
      
      const response = await api.get('/admin/equipment', { params });
      // Handle different response structures
      let equipmentData = [];
      if (Array.isArray(response.data)) {
        equipmentData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        equipmentData = response.data.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        equipmentData = response.data.data;
      }
      setEquipment(equipmentData);
    } catch (error) {
      console.error('Error loading equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setActiveSearch(search);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setSearch('');
    setActiveSearch('');
    setCategoryFilter('all');
    setActiveFilter('all');
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('equipments.confirmDelete'))) return;
    try {
      await api.delete(`/admin/equipment/${id}`);
      loadEquipment();
    } catch (error: any) {
      console.error('Error deleting equipment:', error);
      alert(error.response?.data?.message || t('equipments.failedToDelete'));
    }
  };

  const handleToggleActive = async (equip: Equipment) => {
    try {
      await api.put(`/admin/equipment/${equip._id}`, { active: !equip.active });
      loadEquipment();
    } catch (error: any) {
      console.error('Error updating equipment:', error);
      alert(error.response?.data?.message || t('equipments.failedToUpdate'));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('equipments.title')}</h3>
            <p className="text-sm text-gray-500">{t('equipments.description')}</p>
          </div>
          <button
            onClick={() => navigate('/equipment/new')}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
          >
            <PlusIcon className="w-5 h-5" />
            <span>{t('equipments.addEquipment')}</span>
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col space-y-4">
          <div className="flex space-x-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('equipments.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600"
            >
              {t('common.search')}
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 border rounded-lg flex items-center space-x-2 ${
                showFilters ? 'bg-gray-100 border-gray-300' : 'border-gray-300'
              }`}
            >
              <FunnelIcon className="w-5 h-5" />
              <span>{t('equipments.filters')}</span>
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('equipments.category')}</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="all">{t('equipments.allCategories')}</option>
                  <option value="utensil">{t('equipments.utensil')}</option>
                  <option value="cookware">{t('equipments.cookware')}</option>
                  <option value="appliance">{t('equipments.appliance')}</option>
                  <option value="tool">{t('equipments.tool')}</option>
                  <option value="other">{t('equipments.other')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('equipments.status')}</label>
                <select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value as any)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="all">{t('equipments.all')}</option>
                  <option value="active">{t('equipments.active')}</option>
                  <option value="inactive">{t('equipments.inactive')}</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('equipments.clearFilters')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500">{t('equipments.loading')}</div>
        ) : equipment.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('equipments.noEquipments')}</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('equipments.nameEnAr')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('equipments.icon')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('equipments.category')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('equipments.status')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('equipments.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {equipment.map((equip) => (
                <tr key={equip._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="font-medium text-gray-900">{equip.name?.en || '-'}</div>
                    {equip.name?.ar && (
                      <div className="text-gray-500" dir="rtl">{equip.name.ar}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm w-16">
                    {equip.iconUrl ? (
                      <img
                        src={equip.iconUrl}
                        alt=""
                        className="w-10 h-10 object-contain rounded-lg border border-gray-200 bg-gray-50 flex-shrink-0"
                        title={equip.name?.en}
                      />
                    ) : (
                      <span className="inline-flex w-10 h-10 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{equip.category || 'other'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(equip)}
                      className={`px-2 py-1 text-xs rounded-full ${
                        equip.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {equip.active ? t('equipments.active') : t('equipments.inactive')}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => navigate(`/equipment/${equip._id}/edit`)}
                        className="text-primary-orange hover:text-orange-600"
                        title={t('common.edit')}
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(equip._id)}
                        className="text-red-600 hover:text-red-800"
                        title={t('common.delete')}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

